import { Outlet, useNavigate } from 'react-router-dom'
import { Page } from './page.tsx'
import React, { useEffect, useMemo } from 'react'
import { newRelic } from '../../utils/new-relic.ts'
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts'
import {
  reloadThemeStyle,
  selectTheme,
} from '../../features/setting/setting.slice.ts'
import { Theme } from '../../features/setting/setting.def.ts'
import {
  getDarkComponents,
  getDarkToken,
  getLightComponents,
  getLightToken,
} from '../../styles/antd/config-token/token.ts'
import {
  requestUserInfo,
  selectUser,
  setUserInfo,
} from '../../features/user/user.slice.ts'
import { useLazyGetUserInfoQuery } from '../../apis/auth/auth.api.ts'
import { IdeyaSpinner } from '../components/ideya-spinner.tsx'
import { GlobalNotificationHolder } from '../components/global-notification-holder.tsx'
import ConfigProvider from 'antd/es/config-provider/index'
import theme from 'antd/es/theme/index'
import {
  getDarkComponents1ppon,
  getDarkToken1ppon,
  getLightComponents1ppon,
  getLightToken1ppon,
} from '../../styles/antd/config-token/ippon-token.ts'
import { isIppon } from '../../utils/helpers.ts'
import { useMatomoTrack } from '../../matomo/use-matomo-track.ts'
import { useTranslation } from 'react-i18next'
import { useFixedBreakpoint } from '../hooks/use-is-mobile.ts'

export const Root: React.FC = () => {
  const navigate = useNavigate()
  const colorTheme = useAppSelector(selectTheme)
  const user = useAppSelector(selectUser)
  const dispatch = useAppDispatch()
  const [getUserInfo, { data: userInfoData, isLoading, isError }] =
    useLazyGetUserInfoQuery()
  const { i18n } = useTranslation()
  const language = i18n.language
  useMatomoTrack()
  const screens = useFixedBreakpoint()

  const token = useMemo(
    () =>
      isIppon
        ? colorTheme === Theme.Dark
          ? getDarkToken1ppon()
          : getLightToken1ppon()
        : colorTheme === Theme.Dark
          ? getDarkToken()
          : getLightToken(),
    [isIppon, colorTheme, language]
  )

  const components = useMemo(
    () =>
      isIppon
        ? colorTheme === Theme.Dark
          ? getDarkComponents1ppon()
          : getLightComponents1ppon()
        : colorTheme === Theme.Dark
          ? getDarkComponents()
          : getLightComponents(),
    [isIppon, colorTheme, language]
  )

  // Fetch user info on mount if user is not present
  useEffect(() => {
    if (!user) {
      dispatch(requestUserInfo())
      getUserInfo()
    } else if (!isLoading) {
      // If already on /idea, preserve search params
      if (location.pathname.startsWith('/idea')) {
        navigate(`/idea${location.search}`)
      } else if (location.pathname === '/') {
        navigate('/idea')
      } else {
        navigate(location.pathname + location.search)
      }
    }
  }, [user, dispatch, getUserInfo, isLoading, navigate])

  useEffect(() => {
    if (userInfoData) {
      dispatch(setUserInfo(userInfoData.data))
    }
  }, [userInfoData, dispatch])

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      colorTheme === Theme.Dark ? Theme.Dark : Theme.Light
    )
    reloadThemeStyle()
  }, [colorTheme])

  useEffect(() => {
    if (!user && !isLoading && isError) {
      newRelic.noticeError(new Error('verify failed, redirecting to login'), {
        context: 'rootVerifyToken',
      })
      navigate('/login')
    }
  }, [user, isLoading, isError, navigate])

  if (!user || isLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IdeyaSpinner />
      </div>
    )
  }

  return (
    <ConfigProvider
      theme={{
        cssVar: true,
        token,
        components,
        algorithm:
          colorTheme === Theme.Dark
            ? theme.darkAlgorithm
            : theme.defaultAlgorithm,
      }}
      componentSize={screens.md ? 'small' : 'middle'}
    >
      <GlobalNotificationHolder />
      <div className={'w-full h-full'}>
        <Page userInfo={user}>
          <Outlet />
        </Page>
      </div>
    </ConfigProvider>
  )
}
